import * as ContextMenu from "@radix-ui/react-context-menu";
import {
  IconChevronRight,
  IconCopy,
  IconDownload,
  IconFolderOpen,
  IconInfoCircle,
  IconLink,
  IconPencil,
  IconShare,
  IconTag,
  IconThumbUp,
  IconTrash,
  IconTransfer,
  IconUserShare,
} from "@tabler/icons-react";
import { useFileManager } from "@/components/core/FileManager";
import { useFileManagerUI } from "@/components/core/FileManager";
import type { FileManagerItem } from "@/components/core/FileManager";

import styles from "./FileManagerContextMenu.module.scss";

interface ItemActionProps {
  item: FileManagerItem;
}

export function FileManagerItemOpenAction({ item }: ItemActionProps) {
  const { openFolder, canDownload, resolveDownloadUrl } = useFileManager();

  if (item.type === "FOLDER") {
    return (
      <ContextMenu.Item className={styles.item} onSelect={() => openFolder(item.id, item.name)}>
        <IconFolderOpen size={16} />
        Open
      </ContextMenu.Item>
    );
  }

  if (!canDownload(item)) return null;

  async function openFileItem() {
    const url = await resolveDownloadUrl(item);
    if (url) window.open(url, "_blank", "noopener");
  }

  return (
    <ContextMenu.Item className={styles.item} onSelect={() => void openFileItem()}>
      <IconDownload size={16} />
      Download
    </ContextMenu.Item>
  );
}

export function FileManagerItemRenameAction({ item }: ItemActionProps) {
  const { permissions, isLocked } = useFileManager();
  const { openRename } = useFileManagerUI();

  if (!permissions.canEdit || isLocked(item, "rename")) return null;

  return (
    <ContextMenu.Item
      className={styles.item}
      onSelect={() => openRename({ type: item.type, id: item.id, name: item.name })}
    >
      <IconPencil size={16} />
      Rename
    </ContextMenu.Item>
  );
}

export function FileManagerItemOrganizeActions({ item }: ItemActionProps) {
  const { permissions, isLocked } = useFileManager();
  const { openMove, openDuplicate } = useFileManagerUI();
  const { canEdit } = permissions;

  return (
    <>
      {canEdit && !isLocked(item, "move") && (
        <ContextMenu.Item
          className={styles.item}
          onSelect={() => openMove({ type: item.type, id: item.id, name: item.name })}
        >
          <IconTransfer size={16} />
          Move to...
        </ContextMenu.Item>
      )}
      {canEdit && (
        <ContextMenu.Item
          className={styles.item}
          onSelect={() => openDuplicate({ type: item.type, id: item.id, name: item.name })}
        >
          <IconCopy size={16} />
          Copy to...
        </ContextMenu.Item>
      )}
    </>
  );
}

export function FileManagerItemTagsAction({ item }: ItemActionProps) {
  const { permissions } = useFileManager();
  const { openTags } = useFileManagerUI();

  if (!permissions.canEdit) return null;

  return (
    <ContextMenu.Item
      className={styles.item}
      onSelect={() =>
        openTags({
          id: item.id,
          name: item.name,
          tags: item.tags.map((t) => (typeof t === "string" ? t : t.name)),
        })
      }
    >
      <IconTag size={16} />
      Tags
    </ContextMenu.Item>
  );
}

export function FileManagerItemDetailsAction({ item }: ItemActionProps) {
  const { openDetails } = useFileManagerUI();

  return (
    <ContextMenu.Item className={styles.item} onSelect={() => openDetails(item)}>
      <IconInfoCircle size={16} />
      Details
    </ContextMenu.Item>
  );
}

export function FileManagerItemShareActions({ item }: ItemActionProps) {
  const { getShareLink, canGetShareLink, permissions } = useFileManager();
  const { openShare } = useFileManagerUI();

  async function handleCopyLink() {
    const url = await getShareLink(item);
    await navigator.clipboard.writeText(url);
  }

  if (!permissions.canShare && !canGetShareLink) return null;

  return (
    <ContextMenu.Sub>
      <ContextMenu.SubTrigger className={styles.subTrigger}>
        <IconShare size={16} />
        Share
        <IconChevronRight size={12} className={styles.subArrow} />
      </ContextMenu.SubTrigger>
      <ContextMenu.Portal>
        <ContextMenu.SubContent className={styles.content}>
          {permissions.canShare && (
            <ContextMenu.Item className={styles.item} onSelect={() => openShare(item)}>
              <IconUserShare size={16} />
              Share with people...
            </ContextMenu.Item>
          )}
          {canGetShareLink && (
            <ContextMenu.Item className={styles.item} onSelect={handleCopyLink}>
              <IconLink size={16} />
              Copy link
            </ContextMenu.Item>
          )}
        </ContextMenu.SubContent>
      </ContextMenu.Portal>
    </ContextMenu.Sub>
  );
}

export function FileManagerItemApproveAction({ item }: ItemActionProps) {
  const { permissions } = useFileManager();
  const { openApproval, isSelectionMode, isSelected, selectedItems } = useFileManagerUI();

  if (item.type === "FOLDER" || !permissions.canApprove) return null;

  function handleApprove() {
    const targets =
      isSelectionMode && isSelected(item.id)
        ? selectedItems
            .filter((i) => i.type === "FILE")
            .map((i) => ({ id: i.id, name: i.name, type: i.type }))
        : [{ id: item.id, name: item.name, type: item.type }];
    openApproval({ items: targets });
  }

  return (
    <ContextMenu.Item className={styles.item} onSelect={handleApprove}>
      <IconThumbUp size={16} />
      Mark for Approval
    </ContextMenu.Item>
  );
}

export function FileManagerItemDeleteAction({ item }: ItemActionProps) {
  const { permissions, isLocked } = useFileManager();
  const { openDelete } = useFileManagerUI();

  if (!permissions.canDelete || isLocked(item, "delete")) return null;

  return (
    <ContextMenu.Item
      className={styles.destructiveItem}
      onSelect={() =>
        openDelete({
          items: [{ type: item.type, id: item.id, name: item.name, s3Key: item.s3Key }],
        })
      }
    >
      <IconTrash size={16} />
      Delete
    </ContextMenu.Item>
  );
}

export function FileManagerItemMoveAction({ item }: ItemActionProps) {
  const { permissions, isLocked } = useFileManager();
  const { openMove } = useFileManagerUI();

  if (!permissions.canEdit || isLocked(item, "move")) return null;

  return (
    <ContextMenu.Item
      className={styles.item}
      onSelect={() => openMove({ type: item.type, id: item.id, name: item.name })}
    >
      <IconTransfer size={16} />
      Move {item.type === "FOLDER" ? "folder" : "file"}
    </ContextMenu.Item>
  );
}

export function FileManagerItemCopyAction({ item }: ItemActionProps) {
  const { permissions } = useFileManager();
  const { openDuplicate } = useFileManagerUI();

  if (!permissions.canEdit) return null;

  return (
    <ContextMenu.Item
      className={styles.item}
      onSelect={() => openDuplicate({ type: item.type, id: item.id, name: item.name })}
    >
      <IconCopy size={16} />
      Duplicate {item.type === "FOLDER" ? "folder" : "file"}
    </ContextMenu.Item>
  );
}

export function FileManagerItemDefaultMenuItems({
  item,
  hideApprove,
}: ItemActionProps & { hideApprove?: boolean }) {
  const { permissions } = useFileManager();

  return (
    <>
      <FileManagerItemOpenAction item={item} />

      {permissions.canEdit && (
        <>
          <ContextMenu.Separator className={styles.separator} />
          <FileManagerItemRenameAction item={item} />
        </>
      )}

      <ContextMenu.Separator className={styles.separator} />
      <FileManagerItemOrganizeActions item={item} />

      <ContextMenu.Separator className={styles.separator} />
      <FileManagerItemTagsAction item={item} />
      <FileManagerItemDetailsAction item={item} />
      <FileManagerItemShareActions item={item} />

      {item.type !== "FOLDER" && permissions.canApprove && !hideApprove && (
        <>
          <ContextMenu.Separator className={styles.separator} />
          <FileManagerItemApproveAction item={item} />
        </>
      )}

      {permissions.canDelete && (
        <>
          <ContextMenu.Separator className={styles.separator} />
          <FileManagerItemDeleteAction item={item} />
        </>
      )}
    </>
  );
}
